/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: VoicemailSettingsFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Voicemail Settings module.
----------------------------------------------------------------*/


import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Voicemail, LogOut } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { ModuleFormHeader, ModuleFormLayout } from '../../components/layouts';
import { useVoicemailSettingsData } from './hooks';
import { VoicemailSettingsFormData } from './types';
import { VoicemailGeneralBox } from './components/VoicemailGeneralBox';
import { VoicemailBehaviorBox } from './components/VoicemailBehaviorBox';

export default function VoicemailSettingsFormView() {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showError, showSuccess, showInfo } = useNotifications();
    const lastSaveTimestamp = useRef(0);

    const {
        formData, errors, isLoading, isSaving, hasExisting, hasChanges,
        saveVoicemailSettings, handleFieldChange, resetToOriginal, setErrors,
    } = useVoicemailSettingsData({
        onSuccess: (msg) => showSuccess(msg),
        onError: (err) => showError(err.message),
    });

    const pageTitle = useMemo(() => t('voicemailSettings.editEntry'), [t]);

    useEffect(() => { document.title = `${pageTitle} | Ring2All`; }, [pageTitle]);

    // -- Handlers ----------------------------------------------
    const handleNumberChange = (field: keyof VoicemailSettingsFormData) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const v = Number(e.target.value);
            handleFieldChange(field, Number.isFinite(v) ? v : 0);
        };

    const validateForm = useCallback(() => {
        const errs: Record<string, string> = {};
        const numFields: Array<{ field: keyof VoicemailSettingsFormData; min: number; label: string }> = [
            { field: 'maxMessageLength', min: 1, label: t('voicemailSettings.fields.maxMessageLength.label') },
            { field: 'minMessageLength', min: 0, label: t('voicemailSettings.fields.minMessageLength.label') },
            { field: 'greetingsLength',  min: 1, label: t('voicemailSettings.fields.greetingsLength.label') },
            { field: 'maxSilence',       min: 1, label: t('voicemailSettings.fields.maxSilence.label') },
            { field: 'maxLoginAttempts', min: 1, label: t('voicemailSettings.fields.maxLoginAttempts.label') },
            { field: 'backupDeleted',    min: 0, label: t('voicemailSettings.fields.backupDeleted.label') },
            { field: 'maxMessages',      min: 1, label: t('voicemailSettings.fields.maxMessages.label') },
        ];
        numFields.forEach(({ field, min, label }) => {
            const v = formData[field];
            if (typeof v !== 'number' || Number.isNaN(v)) { errs[field as string] = t('voicemailSettings.validation.numberRequired', { field: label }); return; }
            if (v < min) errs[field as string] = t('voicemailSettings.validation.minValue', { field: label, min });
        });
        if (!formData.emailDateFormat.trim()) errs.emailDateFormat = t('voicemailSettings.validation.emailDateFormatRequired');
        setErrors(errs);
        return Object.keys(errs).length === 0;
    }, [formData, t, setErrors]);

    const handleSubmit = useCallback(async (event?: React.FormEvent) => {
        event?.preventDefault();
        if (!validateForm()) { showError(t('voicemailSettings.messages.validationError')); return; }
        const now = Date.now();
        if (now - lastSaveTimestamp.current < 1000) return;
        lastSaveTimestamp.current = now;
        try { await saveVoicemailSettings(); } catch {}
    }, [hasChanges, hasExisting, saveVoicemailSettings, showError, showInfo, t, validateForm]);

    return (
        <div className="p-6 pb-24">
            <ModuleFormHeader
                icon={<Voicemail className="h-5 w-5 text-primary" />}
                title={pageTitle}
                backButtonText={t('common.exit')}
                backButtonIcon={<LogOut className="h-4 w-4" />}
                onBack={() => navigate(-1)}
                actions={[]}
            />
            <ModuleFormLayout
                onSubmit={(e) => void handleSubmit(e)}
                onCancel={resetToOriginal}
                isSaving={isSaving}
                saveText={t('common.save')}
                cancelText={t('common.cancel')}
                isEditMode={hasExisting}
                wrapChildrenInCard={false}
                className="mt-8"
            >
                <VoicemailGeneralBox
                    formData={formData} errors={errors} isLoading={isLoading}
                    onNumberChange={handleNumberChange} onFieldChange={handleFieldChange}
                />
                <VoicemailBehaviorBox
                    formData={formData} isLoading={isLoading} onFieldChange={handleFieldChange}
                />
            </ModuleFormLayout>
        </div>
    );
}
